// Papel do usuário na conta.
//
// Isto é **experiência**, não fronteira de segurança: quem decide é a RLS. O
// valor daqui serve para não oferecer um botão que o banco vai recusar — e a
// recusa continua existindo, para o caso de alguém chamar a API direto.
//
// A resposta vem das mesmas funções que as policies consultam (`e_gestor`,
// `tem_permissao`), e não de uma cópia da regra no frontend: se a permissão de
// um papel mudar pela tela de permissões, a interface acompanha sem deploy.

import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

const cache = new Map<string, boolean>();
const emVoo = new Map<string, Promise<boolean>>();

async function perguntar(rpc: string, args: Record<string, unknown>): Promise<boolean> {
  const chave = `${rpc}:${JSON.stringify(args)}`;

  const guardado = cache.get(chave);
  if (guardado !== undefined) return guardado;

  const jaPedido = emVoo.get(chave);
  if (jaPedido) return jaPedido;

  // O builder do supabase-js é apenas "thenable": `Promise.resolve` dá acesso a
  // `.finally`, que o tipo original não expõe.
  const promessa = Promise.resolve(supabase.rpc(rpc, args))
    .then(({ data, error }) => {
      if (error) throw new Error(error.message);
      const valor = data === true;
      cache.set(chave, valor);
      return valor;
    })
    .finally(() => emVoo.delete(chave));

  emVoo.set(chave, promessa);
  return promessa;
}

/** Esvazia o cache — ao trocar de sessão ou de conta impersonada. */
export function invalidarPermissoes(): void {
  cache.clear();
}

function useResposta(rpc: string, args: Record<string, unknown>, contexto?: string) {
  const chave = `${rpc}:${JSON.stringify(args)}:${contexto ?? ''}`;
  const [valor, setValor] = useState(false);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);

    perguntar(rpc, args)
      .then((resposta) => {
        if (!cancelado) setValor(resposta);
      })
      .catch(() => {
        // Negar por omissão: numa falha de rede, é melhor esconder a ação do
        // que oferecê-la e receber um erro do banco no clique.
        if (!cancelado) setValor(false);
      })
      .finally(() => {
        if (!cancelado) setCarregando(false);
      });

    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);

  return { valor, carregando };
}

/** O usuário é o Gestor da conta (papel com acesso total)? */
export function useEGestor(contexto?: string) {
  const { valor, carregando } = useResposta('e_gestor', {}, contexto);
  return { eGestor: valor, carregando };
}

/** O usuário tem a permissão indicada (ex.: `condominios.excluir`)? */
export function useTemPermissao(permissao: string, contexto?: string) {
  const { valor, carregando } = useResposta('tem_permissao', { p_permissao: permissao }, contexto);
  return { temPermissao: valor, carregando };
}
